import {
  generators, createMaintenanceActivityIpcRequest, maintenanceActivityReplySchema,
  type MaintenanceActivityRequest, type MaintenanceActivityReply,
  type PlaycastMessage, type PlaycastMessageTarget,
} from '@playcastdotio/Messaging';
import type { IIpcService } from '../ipc/IIpcService';

export interface MaintenanceActivityServiceDependencies {
  ipcService: IIpcService;
  receive(listener: (message: PlaycastMessage<PlaycastMessageTarget>) => void): void | (() => void);
  requestTimeoutMs?: number;
}

type Pending = { action: string; finish(error: Error | null, value?: unknown): void };
const actions = new Set(['maintenanceCapabilities', 'maintenanceSnapshot', 'maintenanceStart', 'maintenanceReconcile',
  'maintenanceActivityReserve', 'maintenanceActivityRenew', 'maintenanceActivityRelease']);

/** Correlation shared by activity/session clients; reuses the existing renderer/native socket. */
export class MaintenanceNativeRequestService {
  private readonly pending = new Map<string, Pending>();
  private readonly unsubscribe: (() => void) | undefined;
  private disposed = false;

  constructor(private readonly deps: MaintenanceActivityServiceDependencies, private readonly prefix = 'maintenance_native') {
    this.unsubscribe = deps.receive(message => this.receive(message)) || undefined;
  }

  call<T>(action: string, body: unknown, parse: (value: unknown) => T): Promise<T> {
    if (this.disposed) return Promise.reject(new Error(`${this.prefix}_disposed`));
    if (!actions.has(action)) return Promise.reject(new Error(`${this.prefix}_unsupported`));
    if (this.pending.size >= 128) return Promise.reject(new Error(`${this.prefix}_busy`));
    const template = generators.system.selfCheckMachineId('host');
    const header = { ...template.header, tag: crypto.randomUUID(), action };
    const message = { ...template, header, signature: btoa(JSON.stringify(header)),
      body: { ...template.body, message: body } } as unknown as PlaycastMessage<PlaycastMessageTarget>;
    return new Promise((resolve, reject) => {
      const finish = (error: Error | null, value?: unknown) => {
        clearTimeout(timer);
        this.pending.delete(header.tag);
        if (error) reject(error);
        else {
          try { resolve(parse(value)); } catch { reject(new Error(`${this.prefix}_invalid_reply`)); }
        }
      };
      const timer = setTimeout(() => finish(new Error(`${this.prefix}_timeout`)), this.deps.requestTimeoutMs ?? 5_000);
      this.pending.set(header.tag, { action, finish });
      try { this.deps.ipcService.send(message); } catch { finish(new Error(`${this.prefix}_unavailable`)); }
    });
  }

  dispose(): void {
    this.disposed = true;
    this.unsubscribe?.();
    for (const item of [...this.pending.values()]) item.finish(new Error(`${this.prefix}_disposed`));
  }

  private receive(message: PlaycastMessage<PlaycastMessageTarget>): void {
    if (this.disposed || !message?.header) return;
    const item = this.pending.get(message.header.tag);
    if (!item || message.header.target !== 'system' || message.header.source !== 'playjector'
      || message.header.isReply !== true || message.header.action !== item.action) return;
    const value = message.body?.message as { success?: boolean; errorCode?: string } | undefined;
    if (value?.success === false) item.finish(new Error(value.errorCode ?? `${this.prefix}_rejected`));
    else item.finish(null, value);
  }
}

/** Typed pre-peer activity admission over the existing IPC channel. */
export class MaintenanceActivityService {
  private readonly requests: MaintenanceNativeRequestService;
  constructor(deps: MaintenanceActivityServiceDependencies) {
    this.requests = new MaintenanceNativeRequestService(deps, 'maintenance_activity');
  }

  async request(request: MaintenanceActivityRequest): Promise<MaintenanceActivityReply> {
    const payload = createMaintenanceActivityIpcRequest(request);
    const reply = await this.requests.call(payload.action, payload.request,
      value => maintenanceActivityReplySchema.parse(value) as MaintenanceActivityReply);
    const permit = reply.permit;
    if (permit && (permit.ownerId !== request.ownerId || permit.kind !== request.kind
      || (request.permitId !== undefined && permit.permitId !== request.permitId)
      || (request.fence !== undefined && permit.fence !== request.fence))) {
      throw new Error('maintenance_activity_owner_mismatch');
    }
    return reply;
  }

  dispose(): void { this.requests.dispose(); }
}
